import {
  ZadDiscountType,
  ZadProduct,
  ZadPurchase,
  ZadPurchaseItemWritePayload,
  ZadPurchaseStatus,
  ZadPurchaseSupplierRef,
  ZadPurchaseUnit,
  ZadPurchaseWritePayload,
} from '../../../models/zad'
import {parseZadStockQuantity} from './stockHelpers'
import {parseZadPriceInput, roundZadMoney3} from './productHelpers'
import {resolveZadUserMessage, ZadApiError} from './errors'
import {
  isZadPackagingEnabled,
  normalizeZadPurchaseUnit,
  zadPurchaseReceivedBaseUnits,
  zadUnitsPerPackage,
} from './packagingHelpers'

export const ZAD_PURCHASE_STATUS_I18N: Record<ZadPurchaseStatus, string> = {
  DRAFT: 'ZAD.PURCHASES.STATUS_DRAFT',
  VALIDATED: 'ZAD.PURCHASES.STATUS_VALIDATED',
  CANCELLED: 'ZAD.PURCHASES.STATUS_CANCELLED',
}

export const ZAD_DISCOUNT_TYPE_I18N: Record<ZadDiscountType, string> = {
  NONE: 'ZAD.PURCHASES.DISCOUNT_NONE',
  PERCENT: 'ZAD.PURCHASES.DISCOUNT_PERCENT',
  FIXED: 'ZAD.PURCHASES.DISCOUNT_FIXED',
}

export type ZadPurchaseEditorItem = {
  key: string
  product: string
  productName: string
  productCode: string
  quantity: string
  listUnitPrice: string
  discountType: ZadDiscountType
  discountValue: string
  vatRate?: string
  purchaseUnit?: ZadPurchaseUnit
  unitsPerPackage?: number
  packagingEnabled?: boolean
}

export type ZadPurchaseEditorValues = {
  supplier: string
  supplierDocumentNumber: string
  purchaseDate: string
  notes: string
  globalDiscountType: ZadDiscountType
  globalDiscountValue: string
  stampAmount: string
  items: ZadPurchaseEditorItem[]
}

export type ZadPurchaseItemPreview = {
  quantity: number
  listUnitPrice: number
  discountType: ZadDiscountType
  discountValue: number
  /** Per-unit discount. Informational for PERCENT. Never multiplied back into lineTotal. */
  discountAmount: number
  /** Informational unit price. Never compute invoice net as quantity × netUnitPrice. */
  netUnitPrice: number
  lineSubtotal: number
  lineDiscountAmount: number
  lineTotal: number
  allocatedGlobalDiscount: number
  taxableNetHT: number
  vatRate: number
  vatAmount: number
  lineTotalTTC: number
}

export type ZadPurchaseTotalsPreview = {
  items: ZadPurchaseItemPreview[]
  subtotal: number
  itemDiscountTotal: number
  afterItemDiscounts: number
  globalDiscountType: ZadDiscountType
  globalDiscountValue: number
  globalDiscountAmount: number
  totalDiscount: number
  netMerchandise: number
  netHT: number
  vatTotal: number
  totalTTCBeforeStamp: number
  stampAmount: number
  amountPayable: number
  payable: number
  total: number
}

export function getZadPurchaseSupplierId(
  supplier: ZadPurchase['supplier'] | string | null | undefined
): string {
  if (!supplier) return ''
  if (typeof supplier === 'string') return supplier
  return supplier._id || ''
}

export function getZadPurchaseSupplierLabel(
  supplier: ZadPurchase['supplier'] | ZadPurchaseSupplierRef | string | null | undefined
): string {
  if (!supplier || typeof supplier === 'string') return '—'
  const name = supplier.name || ''
  const code = supplier.code || ''
  if (name && code) return `${name} — ${code}`
  return name || code || '—'
}

export function getZadPurchaseProductId(product: ZadPurchase['items'][number]['product']): string {
  if (!product) return ''
  if (typeof product === 'string') return product
  return product._id || ''
}

export function isZadPurchaseDraft(status?: ZadPurchaseStatus | string): boolean {
  return status === 'DRAFT'
}

export function canEditZadPurchase(status?: ZadPurchaseStatus | string): boolean {
  return status === 'DRAFT'
}

export function canValidateZadPurchase(status?: ZadPurchaseStatus | string): boolean {
  return status === 'DRAFT'
}

export function canCancelZadPurchase(status?: ZadPurchaseStatus | string): boolean {
  return status === 'DRAFT'
}

export function parseZadPurchaseVatRate(
  value: string | number | null | undefined
): {ok: true; value: number} | {ok: false} {
  if (value === undefined || value === null || value === '') return {ok: true, value: 0}
  const parsed = parseZadPriceInput(value, {required: false, defaultValue: 0})
  if (!parsed.ok || parsed.value > 100) return {ok: false}
  return {ok: true, value: parsed.value}
}

function applyZadPurchaseLineVat(
  item: ZadPurchaseItemPreview,
  vatRate: number
): ZadPurchaseItemPreview {
  const vatAmount = roundZadMoney3((item.taxableNetHT * vatRate) / 100)
  return {
    ...item,
    vatRate,
    vatAmount,
    lineTotalTTC: roundZadMoney3(item.taxableNetHT + vatAmount),
  }
}

export function previewZadPurchaseItem(values: {
  quantity: string | number
  listUnitPrice: string | number
  discountType: ZadDiscountType
  discountValue: string | number
  vatRate?: string | number | null
}): ZadPurchaseItemPreview | null {
  const qty = parseZadStockQuantity(values.quantity)
  const price = parseZadPriceInput(values.listUnitPrice, {required: true})
  const vat = parseZadPurchaseVatRate(values.vatRate)
  if (!qty.ok || !price.ok || !vat.ok) return null
  const type = values.discountType || 'NONE'
  let discountValue = 0
  if (type !== 'NONE') {
    const parsed = parseZadPriceInput(values.discountValue, {required: false, defaultValue: 0})
    if (!parsed.ok) return null
    discountValue = parsed.value
  }
  const list = roundZadMoney3(price.value)
  const lineSubtotal = roundZadMoney3(qty.value * list)
  let lineDiscountAmount = 0
  let perUnitDiscount = 0
  if (type === 'PERCENT') {
    if (discountValue > 100) return null
    lineDiscountAmount = roundZadMoney3((lineSubtotal * discountValue) / 100)
    perUnitDiscount = qty.value > 0 ? roundZadMoney3(lineDiscountAmount / qty.value) : 0
  } else if (type === 'FIXED') {
    perUnitDiscount = roundZadMoney3(discountValue)
    if (perUnitDiscount > list) return null
    lineDiscountAmount = roundZadMoney3(qty.value * perUnitDiscount)
  }
  if (lineDiscountAmount > lineSubtotal) return null
  const lineTotal = roundZadMoney3(lineSubtotal - lineDiscountAmount)
  // Informational only. 90.744 / 48 rounds to 1.891, but 48 × 1.891 is not the invoice net.
  const netUnitPrice = qty.value > 0 ? roundZadMoney3(lineTotal / qty.value) : list
  return applyZadPurchaseLineVat(
    {
      quantity: qty.value,
      listUnitPrice: list,
      discountType: type,
      discountValue: type === 'NONE' ? 0 : discountValue,
      discountAmount: perUnitDiscount,
      netUnitPrice,
      lineSubtotal,
      lineDiscountAmount,
      lineTotal,
      allocatedGlobalDiscount: 0,
      taxableNetHT: lineTotal,
      vatRate: vat.value,
      vatAmount: 0,
      lineTotalTTC: lineTotal,
    },
    vat.value
  )
}

export function getZadPurchaseStampAmount(value?: number | null): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return 0
  return value
}

export function getZadPurchaseNetMerchandise(
  purchase: Pick<ZadPurchase, 'subtotal' | 'totalDiscount' | 'total' | 'stampAmount'> & {
    netHT?: number
  }
): number {
  if (typeof purchase.netHT === 'number' && Number.isFinite(purchase.netHT)) {
    return roundZadMoney3(purchase.netHT)
  }
  if (typeof purchase.subtotal === 'number' && typeof purchase.totalDiscount === 'number') {
    return roundZadMoney3(purchase.subtotal - purchase.totalDiscount)
  }
  return roundZadMoney3((Number(purchase.total) || 0) - getZadPurchaseStampAmount(purchase.stampAmount))
}

export function readStoredZadPurchaseAmount(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null
  return value
}

export function isValidZadStampAmountNumber(value: unknown): boolean {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return false
  const text = String(value)
  if (/e/i.test(text)) {
    return Math.abs(value * 1000 - Math.round(value * 1000)) < 1e-6
  }
  const fraction = text.split('.')[1]
  return !fraction || fraction.length <= 3
}

function allocateZadPurchaseGlobalDiscount(
  items: ZadPurchaseItemPreview[],
  globalDiscountAmount: number
): ZadPurchaseItemPreview[] {
  const base = roundZadMoney3(items.reduce((sum, item) => sum + item.lineTotal, 0))
  if (globalDiscountAmount <= 0 || base <= 0) {
    return items.map((item) =>
      applyZadPurchaseLineVat(
        {...item, allocatedGlobalDiscount: 0, taxableNetHT: item.lineTotal},
        item.vatRate
      )
    )
  }
  let consumed = 0
  return items.map((item, index) => {
    const isLast = index === items.length - 1
    const share = isLast
      ? roundZadMoney3(globalDiscountAmount - consumed)
      : roundZadMoney3((item.lineTotal / base) * globalDiscountAmount)
    if (!isLast) consumed = roundZadMoney3(consumed + share)
    return applyZadPurchaseLineVat(
      {
        ...item,
        allocatedGlobalDiscount: share,
        taxableNetHT: roundZadMoney3(item.lineTotal - share),
      },
      item.vatRate
    )
  })
}

export function previewZadPurchaseTotals(
  items: Array<{
    quantity: string | number
    listUnitPrice: string | number
    discountType: ZadDiscountType
    discountValue: string | number
    vatRate?: string | number | null
  }>,
  globalDiscountType: ZadDiscountType,
  globalDiscountValue: string | number,
  stampAmount: string | number = 0
): ZadPurchaseTotalsPreview | null {
  const previews = items.map((item) => previewZadPurchaseItem(item))
  if (previews.some((item) => !item) || previews.length === 0) return null
  const calculated = previews as ZadPurchaseItemPreview[]
  const subtotal = roundZadMoney3(calculated.reduce((sum, item) => sum + item.lineSubtotal, 0))
  const itemDiscountTotal = roundZadMoney3(
    calculated.reduce((sum, item) => sum + item.lineDiscountAmount, 0)
  )
  const afterItemDiscounts = roundZadMoney3(subtotal - itemDiscountTotal)
  const gType = globalDiscountType || 'NONE'
  let gValue = 0
  if (gType !== 'NONE') {
    const parsed = parseZadPriceInput(globalDiscountValue, {required: false, defaultValue: 0})
    if (!parsed.ok) return null
    gValue = parsed.value
  }
  let globalDiscountAmount = 0
  if (gType === 'PERCENT') {
    if (gValue > 100) return null
    globalDiscountAmount = roundZadMoney3((afterItemDiscounts * gValue) / 100)
  } else if (gType === 'FIXED') {
    if (gValue > afterItemDiscounts) return null
    globalDiscountAmount = roundZadMoney3(gValue)
  }
  const allocated = allocateZadPurchaseGlobalDiscount(calculated, globalDiscountAmount)
  const netMerchandise = roundZadMoney3(afterItemDiscounts - globalDiscountAmount)
  const vatTotal = roundZadMoney3(allocated.reduce((sum, item) => sum + item.vatAmount, 0))
  const totalTTCBeforeStamp = roundZadMoney3(
    allocated.reduce((sum, item) => sum + item.lineTotalTTC, 0)
  )
  const stampParsed = parseZadPriceInput(stampAmount ?? 0, {required: false, defaultValue: 0})
  if (!stampParsed.ok) return null
  const stamp = roundZadMoney3(stampParsed.value)
  const payable = roundZadMoney3(totalTTCBeforeStamp + stamp)
  return {
    items: allocated,
    subtotal,
    itemDiscountTotal,
    afterItemDiscounts,
    globalDiscountType: gType,
    globalDiscountValue: gType === 'NONE' ? 0 : gValue,
    globalDiscountAmount,
    totalDiscount: roundZadMoney3(itemDiscountTotal + globalDiscountAmount),
    netMerchandise,
    netHT: netMerchandise,
    vatTotal,
    totalTTCBeforeStamp,
    stampAmount: stamp,
    amountPayable: payable,
    payable,
    total: payable,
  }
}

export function emptyZadPurchaseEditorValues(): ZadPurchaseEditorValues {
  const today = new Date()
  const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(
    today.getDate()
  ).padStart(2, '0')}`
  return {
    supplier: '',
    supplierDocumentNumber: '',
    purchaseDate: iso,
    notes: '',
    globalDiscountType: 'NONE',
    globalDiscountValue: '0',
    stampAmount: '0.000',
    items: [],
  }
}

export function toZadPurchaseEditorValues(purchase?: ZadPurchase | null): ZadPurchaseEditorValues {
  if (!purchase) return emptyZadPurchaseEditorValues()
  const date = purchase.purchaseDate ? new Date(purchase.purchaseDate) : new Date()
  const iso = Number.isNaN(date.getTime())
    ? emptyZadPurchaseEditorValues().purchaseDate
    : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
        date.getDate()
      ).padStart(2, '0')}`
  return {
    supplier: getZadPurchaseSupplierId(purchase.supplier),
    supplierDocumentNumber: purchase.supplierDocumentNumber || '',
    purchaseDate: iso,
    notes: purchase.notes || '',
    globalDiscountType: purchase.globalDiscountType || 'NONE',
    globalDiscountValue: String(purchase.globalDiscountValue ?? 0),
    stampAmount:
      purchase.stampAmount == null ? '0.000' : String(getZadPurchaseStampAmount(purchase.stampAmount)),
    items: (purchase.items || []).map((item, index) => ({
      key: item._id || `item-${index}`,
      product: getZadPurchaseProductId(item.product),
      productName: item.productName || '',
      productCode: item.productCode || '',
      quantity: String(item.quantity ?? ''),
      listUnitPrice: String(item.listUnitPrice ?? ''),
      discountType: item.discountType || 'NONE',
      discountValue: String(item.discountValue ?? 0),
      vatRate: item.vatRate == null ? '0' : String(item.vatRate),
      purchaseUnit: normalizeZadPurchaseUnit(item.purchaseUnit),
      unitsPerPackage:
        item.unitsPerPackage != null
          ? Number(item.unitsPerPackage)
          : item.unitsPerPackageSnapshot != null
            ? Number(item.unitsPerPackageSnapshot)
            : undefined,
      packagingEnabled:
        Boolean(item.unitsPerPackage || item.unitsPerPackageSnapshot) ||
        normalizeZadPurchaseUnit(item.purchaseUnit) === 'PACKAGE',
    })),
  }
}

export function validateZadPurchaseForm(values: ZadPurchaseEditorValues): string | null {
  if (!String(values.supplier || '').trim()) return 'ZAD.PURCHASES.SUPPLIER_REQUIRED'
  if (!String(values.purchaseDate || '').trim()) return 'ZAD.PURCHASES.DATE_REQUIRED'
  if (!values.items.length) return 'ZAD.PURCHASES.ITEMS_REQUIRED'
  const products = new Set<string>()
  for (const item of values.items) {
    if (!item.product) return 'ZAD.PURCHASES.PRODUCT_REQUIRED'
    if (products.has(item.product)) return 'ZAD.PURCHASES.DUPLICATE_PRODUCT'
    products.add(item.product)
    const qty = parseZadStockQuantity(item.quantity)
    if (!qty.ok) return qty.errorId
    const price = parseZadPriceInput(item.listUnitPrice, {required: true})
    if (!price.ok) return 'ZAD.PURCHASES.PRICE_REQUIRED'
    if (item.discountType !== 'NONE') {
      const discount = parseZadPriceInput(item.discountValue, {required: false, defaultValue: 0})
      if (!discount.ok) return 'ZAD.PURCHASES.DISCOUNT_INVALID'
    }
    const vat = parseZadPurchaseVatRate(item.vatRate)
    if (!vat.ok) return 'ZAD.PURCHASES.VAT_INVALID'
    if (!previewZadPurchaseItem(item)) return 'ZAD.PURCHASES.DISCOUNT_INVALID'
    if (normalizeZadPurchaseUnit(item.purchaseUnit) === 'PACKAGE') {
      const upp = Number(item.unitsPerPackage)
      if (!Number.isInteger(upp) || upp < 2) return 'ZAD.PACKAGING.INVALID'
    }
  }
  if (values.globalDiscountType !== 'NONE') {
    const global = parseZadPriceInput(values.globalDiscountValue, {required: false, defaultValue: 0})
    if (!global.ok) return 'ZAD.PURCHASES.DISCOUNT_INVALID'
  }
  const stamp = parseZadPriceInput(values.stampAmount, {required: false, defaultValue: 0})
  if (!stamp.ok) return 'ZAD.PURCHASES.STAMP_INVALID'
  return null
}

function parsedPrice(value: string | number, required = false): number {
  const parsed = parseZadPriceInput(value, {required, defaultValue: 0})
  return parsed.ok ? parsed.value : 0
}

function parsedQuantity(value: string | number): number {
  const parsed = parseZadStockQuantity(value)
  return parsed.ok ? parsed.value : 0
}

function parsedVatRate(value: string | number | undefined): number {
  const parsed = parseZadPurchaseVatRate(value)
  if (parsed.ok) return parsed.value
  return Number(value)
}

export function sanitizeZadPurchaseWritePayload(
  payload: ZadPurchaseWritePayload
): ZadPurchaseWritePayload {
  return {
    supplier: String(payload.supplier || '').trim(),
    supplierDocumentNumber: String(payload.supplierDocumentNumber || '').trim(),
    purchaseDate: String(payload.purchaseDate || '').trim(),
    notes: String(payload.notes || '').trim(),
    globalDiscountType: payload.globalDiscountType || 'NONE',
    globalDiscountValue:
      payload.globalDiscountType === 'NONE' ? 0 : Number(payload.globalDiscountValue) || 0,
    stampAmount: Number.isFinite(Number(payload.stampAmount)) ? Number(payload.stampAmount) || 0 : 0,
    items: (payload.items || []).map((item) => ({
      product: String(item.product || '').trim(),
      quantity: Number(item.quantity) || 0,
      listUnitPrice: Number(item.listUnitPrice) || 0,
      discountType: item.discountType || 'NONE',
      discountValue: item.discountType === 'NONE' ? 0 : Number(item.discountValue) || 0,
      vatRate: Number.isFinite(Number(item.vatRate)) ? Number(item.vatRate) : 0,
      ...(normalizeZadPurchaseUnit(item.purchaseUnit) === 'PACKAGE'
        ? {
            purchaseUnit: 'PACKAGE' as const,
            unitsPerPackage: Number(item.unitsPerPackage) || undefined,
          }
        : {}),
    })),
  }
}

export function toZadPurchasePayload(values: ZadPurchaseEditorValues): ZadPurchaseWritePayload {
  return {
    supplier: String(values.supplier || '').trim(),
    supplierDocumentNumber: String(values.supplierDocumentNumber || '').trim(),
    purchaseDate: String(values.purchaseDate || '').trim(),
    notes: String(values.notes || '').trim(),
    globalDiscountType: values.globalDiscountType || 'NONE',
    globalDiscountValue: values.globalDiscountType === 'NONE' ? 0 : parsedPrice(values.globalDiscountValue),
    stampAmount: parsedPrice(values.stampAmount),
    items: values.items.map(
      (item): ZadPurchaseItemWritePayload => ({
        product: item.product,
        quantity: parsedQuantity(item.quantity),
        listUnitPrice: parsedPrice(item.listUnitPrice, true),
        discountType: item.discountType || 'NONE',
        discountValue: item.discountType === 'NONE' ? 0 : parsedPrice(item.discountValue),
        vatRate: parsedVatRate(item.vatRate),
        ...(normalizeZadPurchaseUnit(item.purchaseUnit) === 'PACKAGE'
          ? {
              purchaseUnit: 'PACKAGE' as const,
              unitsPerPackage: Number(item.unitsPerPackage) || undefined,
            }
          : {}),
      })
    ),
  }
}

export function isDuplicateZadPurchaseProduct(
  items: Array<{product: string}>,
  productId: string
): boolean {
  return items.some((item) => item.product && item.product === productId)
}

export function formatZadDate(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('fr-FR')
}

export function formatZadItemDiscountLabel(
  type: ZadDiscountType,
  value: number,
  formatMoney: (amount: number) => string
): string {
  if (type === 'PERCENT') return `${value} %`
  if (type === 'FIXED') return `${formatMoney(value)} / unité`
  return '—'
}

export function createZadPurchaseEditorItem(product: ZadProduct): ZadPurchaseEditorItem {
  return {
    key: `${product._id}-${Date.now()}`,
    product: product._id,
    productName: product.name || '',
    productCode: product.code || '',
    quantity: '1',
    listUnitPrice: product.purchasePrice == null ? '' : String(product.purchasePrice),
    discountType: 'NONE',
    discountValue: '0',
    vatRate: '0',
    purchaseUnit: 'UNIT',
    unitsPerPackage: zadUnitsPerPackage(product),
    packagingEnabled: isZadPackagingEnabled(product),
  }
}

export function zadPurchaseLineReceivedUnits(item: ZadPurchaseEditorItem): number {
  return zadPurchaseReceivedBaseUnits(item.quantity, item.purchaseUnit, item.unitsPerPackage)
}

export function resolveZadPurchaseMessage(err: ZadApiError): {i18nId: string; fallback: string} {
  const map: Record<string, string> = {
    SUPPLIER_NOT_FOUND: 'ZAD.PURCHASES.SUPPLIER_NOT_FOUND',
    SUPPLIER_INACTIVE: 'ZAD.PURCHASES.SUPPLIER_INACTIVE',
    PRODUCT_NOT_FOUND: 'ZAD.PURCHASES.PRODUCT_NOT_FOUND',
    PRODUCT_INACTIVE: 'ZAD.STOCK.PRODUCT_INACTIVE',
    CATEGORY_INACTIVE: 'ZAD.PRODUCTS.CATEGORY_INACTIVE',
    DUPLICATE_PRODUCT: 'ZAD.PURCHASES.DUPLICATE_PRODUCT',
    INVALID_DISCOUNT: 'ZAD.PURCHASES.DISCOUNT_INVALID',
    INVALID_VAT: 'ZAD.PURCHASES.VAT_INVALID',
    INVALID_VAT_RATE: 'ZAD.PURCHASES.VAT_INVALID',
    INVALID_QUANTITY: 'ZAD.PURCHASES.QUANTITY_INVALID',
    INVALID_UNIT_PRICE: 'ZAD.PURCHASES.PRICE_REQUIRED',
    PURCHASE_NOT_EDITABLE: 'ZAD.PURCHASES.NOT_EDITABLE',
    PURCHASE_ALREADY_VALIDATED: 'ZAD.PURCHASES.ALREADY_VALIDATED',
    PURCHASE_NOT_VALIDATABLE: 'ZAD.PURCHASES.NOT_VALIDATABLE',
    PRODUCT_TYPE_INACTIVE: 'ZAD.PRODUCTS.TYPE_INACTIVE',
    INVALID_PACKAGING: 'ZAD.PACKAGING.INVALID',
    INVALID_PURCHASE_UNIT: 'ZAD.PACKAGING.INVALID',
  }
  if (err.errorCode && map[err.errorCode]) {
    return {i18nId: map[err.errorCode], fallback: err.error}
  }
  if (/vatRate|vat rate|taux de tva|taux tva/i.test(err.error || '')) {
    return {i18nId: 'ZAD.PURCHASES.VAT_INVALID', fallback: err.error}
  }
  return resolveZadUserMessage(err)
}

export function formatZadVatRate(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—'
  const rounded = roundZadMoney3(value)
  const text = String(rounded)
    .replace(/(\.\d*?)0+$/, '$1')
    .replace(/\.$/, '')
  return `${text}%`
}
